import camelcaseKeys from 'camelcase-keys'
import { createStore } from 'zustand'

import { executeDelete, executeGet, executePost } from '@/services/lukita'
import { REPORT_STATUSES } from '../constants'
import type {
  Analysis,
  AnalysisDTO,
  AnalysisPayload,
  AnalysisStatus,
  ReportProgress,
} from './types'

interface AnalysisStoreActions {
  setAnalysisStatus: (status: AnalysisStatus) => void
  setAnalysis: (analysis: Analysis | AnalysisDTO) => void
  setAnalysisProgress: (progress: ReportProgress) => void
  setIsLoading: (isLoading: boolean) => void
}

type AnalysisStoreMutations = {
  createAnalysis: (slug: string, payload?: AnalysisPayload) => Promise<void>
  deleteAnalysis: (slug: string) => Promise<void>
}

interface AnalysisStoreLoaders {
  fetchAnalysis: (
    slug: string,
    options?: { format: 'csv' | 'docx'; isFile: boolean },
  ) => Promise<Response | [Blob, string | null] | void>
}

export interface AnalysisStore {
  loadingAnalysis: boolean
  mutating: boolean
  analysis: Analysis | AnalysisDTO | null
  actions: AnalysisStoreActions
  mutations: AnalysisStoreMutations
  loaders: AnalysisStoreLoaders
}

export const createAnalysisStore = () =>
  createStore<AnalysisStore>((set, get) => ({
    loadingAnalysis: true,
    mutating: false,
    analysis: null,
    actions: {
      setAnalysis: (analysis) => set({ analysis: camelcaseKeys(analysis) }),
      setAnalysisStatus: (status) =>
        set({ analysis: { ...(get().analysis as Analysis), status } }),
      // Snapshots are seq-ordered full state; an older frame arriving
      // late must never overwrite a newer one.
      setAnalysisProgress: (progress) => {
        const analysis = get().analysis as Analysis | null
        if (!analysis) return
        const currentSeq = analysis.progress?.seq ?? -1
        if ((progress?.seq ?? 0) < currentSeq) return
        set({ analysis: { ...analysis, progress } })
      },
      setIsLoading: (isLoading) => set({ loadingAnalysis: isLoading }),
    },

    mutations: {
      createAnalysis: async (slug, payload = {}) => {
        const previousAnalysis = get().analysis as Analysis
        set({ mutating: true })
        get().actions.setAnalysisStatus(REPORT_STATUSES.generating)

        try {
          await executePost(`/projects/${slug}/analysis`, {
            prompt_analysis: payload,
          })
        } catch (error) {
          get().actions.setAnalysis(previousAnalysis)
          throw error
        } finally {
          set({ mutating: false })
        }
      },

      deleteAnalysis: async (slug) => {
        const previousAnalysis = get().analysis as Analysis

        get().actions.setAnalysisStatus(REPORT_STATUSES.notStarted)

        try {
          await executeDelete(`/projects/${slug}/analysis`)
        } catch (error) {
          get().actions.setAnalysis(previousAnalysis)
          throw error
        }
      },
    },

    loaders: {
      fetchAnalysis: async (slug, options) => {
        let path = `/projects/${slug}/analysis`
        const { setAnalysis, setIsLoading } = get().actions
        if (options?.format) path += `.${options.format}`

        if (!options?.isFile) {
          setIsLoading(true)
        }

        const response = await executeGet(path, options)

        if (options?.isFile) {
          return response
        }
        const analysis = await response.json()

        setAnalysis(analysis)
        setIsLoading(false)
      },
    },
  }))
